import { getCookie } from '~/utils/cookie'

const BASE_URL = '/api/packs'

const csrftoken = (): string => getCookie('csrftoken') ?? ''

/** Distinct trait values still drawable from a pack, so the opening reels can
 *  spin through real possibilities. Values only, never counts: the odds stay
 *  server-side. */
export type PackPool = {
    weapon_types: string[]
    rarities: string[]
    erbs: string[]
}

export type Pack = {
    /** Cover art key, chosen in team tools. Absent on an older pack. */
    art?: string
    id: number
    name: string
    price: string
    currency: string
    /** Staff switch: has this launched at all. */
    is_enabled: boolean
    /** Derived: are there unclaimed weapons left. */
    in_stock: boolean
    stock: number
    /** Never runs out: a skin pack with no supply set. `stock` holds a
     *  sentinel in that case and must not be shown as a count. */
    unlimited?: boolean
    /** Trait values still drawable. The honest "what can I win" answer. */
    pool: PackPool
    /** Trait values in the pack at all, kept or not. Reel scenery only, so a
     *  nearly-empty pack still animates as a draw. Never an availability
     *  claim: read `pool` and `stock` for that. */
    decoys: PackPool
}

export type PackWeapon = {
    /** Null for a skin: it is a row in our database, not a token on Solana.
     *  Typed as always-present before, which is why nothing caught the reveal
     *  calling `.slice` on it and blacking out the screen after payment. */
    mint: string | null
    name: string | null
    weapon_type: string | null
    rarity: string | null
    /** Earnings Retention Bonus, e.g. "10%". The trait that decides whether a
     *  weapon is worth keeping, so the reveal builds up to it. */
    erb: string | null
    gif_uri: string | null
}

export type PackSession = {
    session_id: number
    uuid: string
    pack_id: number
    status: string
    respin_count: number
    original_price_paid: string
    respin_price: string
    asset: PackWeapon | null
    /** Only present on keep. `false` means another player claimed this weapon
     *  first: nothing was charged, and `asset` is already the free re-roll. */
    kept?: boolean
    reason?: string
    previous_asset?: PackWeapon | null
    delivery?: { uuid: string; status: string; address: string }
}

export class PacksApiError extends Error {
    /** Service-layer code, so copy is chosen here rather than rendering
     *  whatever sentence the backend happened to raise. */
    code: string
    status: number

    constructor(message: string, code: string, status: number) {
        super(message)
        this.code = code
        this.status = status
    }
}

const request = async (url: string, method: 'GET' | 'POST'): Promise<any> => {
    const response = await fetch(url, {
        method,
        headers: method === 'POST' ? { 'X-CSRFToken': csrftoken() } : {},
    })

    let body: any = null
    try {
        body = await response.json()
    } catch {
        // The access gate answers with HTML, not JSON.
    }

    // 409 is not a failure. Keep returns it when someone else claimed the
    // weapon first, and the body is a usable session on a new one.
    if (!response.ok && response.status !== 409) {
        throw new PacksApiError(
            body?.message ?? `HTTP ${response.status}`,
            body?.code ?? (response.status === 403 ? 'forbidden' : 'http_error'),
            response.status,
        )
    }
    return body
}

/** One row of the live pulls feed: a weapon another player kept, and the pack
 *  price they paid. Powers the ticker at the top of the page. */
export type PackPull = {
    session_uuid: string
    name: string | null
    rarity: string | null
    weapon_type: string | null
    gif_uri: string | null
    pack_name: string
    price: string
    currency: string
    kept_at: string
}

export const listPacks = async (): Promise<Pack[]> => (await request(`${BASE_URL}/`, 'GET')).items

export const listRecentPulls = async (): Promise<PackPull[]> =>
    (await request(`${BASE_URL}/feed/`, 'GET')).items

/** Every weapon still drawable from a pack — the "what's inside" pool. */
export const listPackContents = async (packId: number): Promise<PackWeapon[]> =>
    (await request(`${BASE_URL}/${packId}/contents/`, 'GET')).items

export const openPack = (packId: number): Promise<PackSession> =>
    request(`${BASE_URL}/${packId}/open/`, 'POST')

export const respinSession = (sessionId: number): Promise<PackSession> =>
    request(`${BASE_URL}/session/${sessionId}/respin/`, 'POST')

export const keepSession = (sessionId: number): Promise<PackSession> =>
    request(`${BASE_URL}/session/${sessionId}/keep/`, 'POST')

/** Player-facing copy per service error code.
 *
 *  Kept in one place so the same failure never reads two different ways, and
 *  so a code the backend adds later falls back to something harmless rather
 *  than leaking an internal sentence. */
export const errorCopy = (code: string, fallback: string): string => {
    switch (code) {
        case 'payment_failed':
            return "You don't have enough balance for this pack."
        case 'pack_unavailable':
            return 'This pack is not available right now.'
        case 'no_delivery_wallet':
            // The pack was refused before anything was charged -- the check now
            // runs at open rather than at keep -- so the copy says what to do
            // rather than apologising for a purchase.
            return 'Connect a Solana wallet to your account before opening this pack. The weapon is sent on-chain, so there has to be somewhere to send it.'
        case 'invalid_session_state':
            return 'This pack has already been finished.'
        case 'forbidden':
            return 'Packs is staff-only right now.'
        default:
            return fallback || 'Something went wrong. Please try again.'
    }
}
